import { useState } from 'react';
import { Button } from './Button/Button';
import { Modal } from './Modal/Modal';
import { useUiStore } from '../store/ui';
import { useCreateYear, useYears } from '../store/queryHooks';
import styles from './CopyYearModal.module.css';

interface Props {
  onClose(): void;
}

export function CopyYearModal({ onClose }: Props) {
  const { year, setYear, setToast } = useUiStore();
  const years = useYears();
  const create = useCreateYear();
  const existing = years.data ?? [year];
  const defaultTarget = Math.max(...existing) + 1;
  const [target, setTarget] = useState(String(defaultTarget));

  async function onCopy() {
    const t = parseInt(target.replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(t) || t < 2000 || t > 2100) {
      setToast('Vælg et gyldigt årstal');
      return;
    }
    if (existing.includes(t)) {
      setToast(`År ${t} findes allerede`);
      return;
    }
    try {
      await create.mutateAsync({ year: t, from: year });
      setToast(`Budget kopieret til ${t} (faktiske tal nulstillet)`);
      setYear(t);
      onClose();
    } catch {
      setToast('Kunne ikke kopiere år');
    }
  }

  return (
    <Modal
      title="Kopiér til nyt år"
      subtitle={`Opret næste års budget ud fra ${year}. Budgettallene kopieres — de faktiske tal starter blanke.`}
      maxWidth={430}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annullér
          </Button>
          <Button variant="primary" onClick={onCopy} disabled={create.isPending}>
            {create.isPending ? 'Opretter…' : 'Opret budget'}
          </Button>
        </>
      }
    >
      <div className={styles.row}>
        <div className={`${styles.side} ${styles.from}`}>
          <div className={styles.label}>Fra</div>
          <div className={styles.year}>{year}</div>
        </div>
        <div className={styles.arrow}>→</div>
        <div className={`${styles.side} ${styles.to}`}>
          <div className={styles.label}>Til</div>
          <input
            className={styles.yearInput}
            inputMode="numeric"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
